import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, Pencil, Columns2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KbCompareDialog } from "@/components/KbCompareDialog";

const kb = (n) => `${(n / 1024).toFixed(0)} KB`;

/** The articles whose imported markup has been tidied, with a side-by-side
 *  before/after so an editor can satisfy themselves nothing was lost. */
export default function KBCleaned() {
  const [data, setData] = useState(null);
  const [compare, setCompare] = useState(null);
  const [loadingCmp, setLoadingCmp] = useState(false);

  const load = useCallback(() => {
    api.get("/kb/cleaned").then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load the list"));
  }, []);
  useEffect(load, [load]);

  const openCompare = async (row) => {
    if (!row.rev_id) { toast.error("No saved copy for this one"); return; }
    setLoadingCmp(true);
    try {
      const { data: c } = await api.get(`/kb/articles/${row.slug}/compare/${row.rev_id}`);
      setCompare(c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the comparison");
    } finally { setLoadingCmp(false); }
  };

  if (!data) {
    return <div className="kb-surface flex h-64 items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" />
    </div>;
  }

  return (
    <div className="kb-surface" data-testid="kb-cleaned-page">
      <Link to="/kb" className="mb-4 flex w-fit items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary"
        data-testid="kb-cleaned-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Knowledge Base
      </Link>
      <h1 className="font-heading text-3xl font-bold text-slate-900 dark:text-slate-100">Recently cleaned</h1>
      <p className="mt-1.5 max-w-3xl text-sm text-slate-500 dark:text-slate-400" data-testid="kb-cleaned-subtitle">
        {data.count} article{data.count === 1 ? "" : "s"} have had their imported Word, Helpjuice and
        Intercom markup stripped — {kb(data.bytes_removed)} of it. Every one keeps a
        “Before HTML cleanup” copy, so open the comparison and restore it from History if anything
        looks wrong.
      </p>

      <div className="mt-6 space-y-2">
        {data.articles.map((r) => {
          const saved = (r.before?.bytes || 0) - r.after.bytes;
          const pct = r.before?.bytes ? Math.round((saved / r.before.bytes) * 100) : 0;
          return (
            <Card key={r.slug} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5"
              data-testid={`kb-cleaned-row-${r.slug}`}>
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-heading text-sm font-semibold text-slate-800 dark:text-slate-100">{r.name}</h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {r.category}
                  {r.draft && <span className="ml-2 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400">DRAFT</span>}
                  <span className="ml-2">
                    {r.before ? <>{kb(r.before.bytes)} → <span className="font-semibold text-emerald-700">{kb(r.after.bytes)}</span> ({pct}% lighter)</>
                      : kb(r.after.bytes)}
                  </span>
                  <span className="ml-2 text-slate-400 dark:text-slate-500">cleaned {(r.cleaned_at || "").slice(0, 10)}</span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openCompare(r)}
                  disabled={loadingCmp} data-testid={`kb-cleaned-compare-${r.slug}`}>
                  <Columns2 className="mr-1.5 h-3.5 w-3.5" /> Compare
                </Button>
                <Link to={`/kb/a/${r.slug}`} data-testid={`kb-cleaned-open-${r.slug}`}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary">
                  <ExternalLink className="h-3.5 w-3.5" /> Open
                </Link>
                <Link to={`/kb/a/${r.slug}/edit`} data-testid={`kb-cleaned-edit-${r.slug}`}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
              </div>
            </Card>
          );
        })}
        {data.count === 0 && (
          <Card className="px-5 py-10 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="kb-cleaned-empty">
            Nothing has been cleaned yet.
          </Card>
        )}
      </div>

      {compare && <KbCompareDialog c={compare} onClose={() => setCompare(null)} />}
    </div>
  );
}
